import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  CheckCircle2, ArrowRight, ArrowUpRight, ChevronRight, Clock,
} from "lucide-react";
import { AwsArchitectureShowcase } from "@/components/AwsArchitectureShowcase";
import { ComplianceHeroSection } from "@/components/ComplianceFrameworksDisplay";
import {
  C, fadeUp, stagger,
  painPoints, platformTabs, comparisonRows, roiStats,
  pipelineSteps, testimonials, paths, resources,
} from "./data";

export const Features = ({ navigate, onLogin }) => (
  <>
    {/* PAIN POINTS */}
    <section className="py-24 px-6" data-testid="pain-points-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="mb-16">
          <motion.p variants={fadeUp} className="text-xs font-mono tracking-widest uppercase mb-3" style={{ color: C.text4 }}>The Problem</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Governance is slowing you down<span style={{ color: C.green }}>.</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base max-w-2xl" style={{ color: C.text3 }}>
            Managing cloud architecture at scale with manual processes causes delays, increases waste, and introduces risk.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {painPoints.map((point, i) => {
            const Icon = point.icon;
            return (
              <motion.div key={i} variants={fadeUp} custom={i}
                className="group rounded-xl p-7 transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBorder}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                data-testid={`pain-point-${i}`}
              >
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: C.redDim }}>
                  <Icon className="w-5 h-5" style={{ color: C.red }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: C.text1 }}>{point.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.text3 }}>{point.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>

    {/* ONE PLATFORM */}
    <section id="platform" className="py-20 px-6" style={{ background: C.dark }} data-testid="platform-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="text-xs font-mono tracking-widest uppercase mb-3" style={{ color: C.green }}>Platform</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ color: C.darkText1 }}>
            One Platform<span style={{ color: C.green }}>.</span> Total Architecture Visibility<span style={{ color: C.green }}>.</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base max-w-2xl mx-auto" style={{ color: C.darkText3 }}>
            Everything you need to design, audit, and export production-ready cloud architecture — in one workflow.
          </motion.p>
        </motion.div>

        <div className="space-y-10">
          {platformTabs.map((feature, i) => {
            const Icon = feature.icon;
            const isReversed = i % 2 === 1;
            return (
              <motion.div key={feature.id} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp} custom={i}
                className="rounded-xl overflow-hidden"
                style={{ background: C.darkSurface, border: `1px solid ${C.darkBorder}` }}
                data-testid={`platform-feature-${feature.id}`}
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${isReversed ? '' : ''}`}>
                  <div className={`relative flex items-center justify-center p-6 ${isReversed ? 'lg:order-2' : ''}`}
                    style={{ background: 'rgba(0,0,0,0.2)', borderRight: isReversed ? 'none' : `1px solid ${C.darkBorder}`, borderLeft: isReversed ? `1px solid ${C.darkBorder}` : 'none' }}
                  >
                    {feature.id === "resilience" ? (
                      <div className="w-full"><AwsArchitectureShowcase /></div>
                    ) : (
                      <img src={feature.image} alt={feature.heading} className="w-full h-auto rounded-lg" loading="lazy" style={{ maxHeight: '340px', objectFit: 'contain' }} />
                    )}
                  </div>
                  <div className={`p-8 lg:p-10 flex flex-col justify-center ${isReversed ? 'lg:order-1' : ''}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(0,109,67,0.15)' }}>
                      <Icon className="w-5 h-5" style={{ color: C.green }} />
                    </div>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: C.darkText1 }}>{feature.heading}</h3>
                    <p className="text-sm leading-relaxed mb-5" style={{ color: C.darkText3 }}>{feature.desc}</p>
                    <ul className="space-y-2.5 mb-6">
                      {feature.bullets.map((b, j) => (
                        <li key={j} className="flex items-center gap-2.5 text-sm" style={{ color: C.darkText2 }}>
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: C.green }} />{b}
                        </li>
                      ))}
                    </ul>
                    <Button className="self-start text-sm rounded-md font-medium" style={{ background: 'transparent', border: `1px solid ${C.darkBorder}`, color: C.darkText2 }}
                      onClick={() => navigate('/wizard')}
                    >
                      Explore {feature.label} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1.5 h-6 rounded-full" style={{ background: C.green }} />
            <h3 className="text-lg font-semibold" style={{ color: C.darkText1 }}>Live Interactive Architecture Diagram</h3>
            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(0,109,67,0.15)', color: C.green }}>HOVER FOR DETAILS</span>
          </div>
          <AwsArchitectureShowcase />
        </div>
      </div>
    </section>

    {/* CERTIFIED PIPELINE */}
    <section className="py-24 px-6" style={{ borderTop: `1px solid ${C.border}` }} data-testid="pipeline-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
          <motion.p variants={fadeUp} className="text-xs font-mono tracking-widest uppercase mb-3" style={{ color: C.green }}>How It Works</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Define<span style={{ color: C.green }}>.</span> Audit<span style={{ color: C.green }}>.</span> Remediate<span style={{ color: C.green }}>.</span> Certify<span style={{ color: C.green }}>.</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base max-w-2xl" style={{ color: C.text3 }}>
            Automated governance across the entire architecture lifecycle. From concept to certified IaC in minutes.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pipelineSteps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="group rounded-xl p-7 transition-all duration-300 hover:-translate-y-0.5 relative"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBorder}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                data-testid={`pipeline-step-${i}`}
              >
                <span className="text-4xl font-bold block mb-4" style={{ color: C.green, opacity: 0.6 }}>{step.num}</span>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: C.greenDim }}>
                  <Icon className="w-5 h-5" style={{ color: C.green }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: C.text1 }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.text3 }}>{step.desc}</p>
                {i < 3 && <ChevronRight className="absolute top-1/2 -right-3 w-5 h-5 hidden lg:block" style={{ color: C.text4, transform: 'translateY(-50%)' }} />}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  </>
);

const ComparisonRow = ({ row, isLast }) => (
  <tr style={{ borderBottom: `1px solid ${C.border}`, background: isLast ? 'rgba(0,109,67,0.03)' : 'transparent', fontWeight: isLast ? 'bold' : 'normal' }}>
    <td className="p-5" style={{ color: C.text2 }}>{row.task}</td>
    <td className="p-5 text-center font-mono" style={{ color: '#DC2626' }}>{row.manual}</td>
    <td className="p-5 text-center font-mono" style={{ color: '#B45309' }}>{row.other}</td>
    <td className="p-5 text-center font-mono font-semibold" style={{ color: C.green, background: C.greenDim }}>{row.zerozone}</td>
  </tr>
);

export const ComparisonAndBeyond = ({ navigate, onLogin }) => (
  <>
    {/* COMPARISON */}
    <section className="py-24 px-6" style={{ borderTop: `1px solid ${C.border}` }} data-testid="comparison-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md mb-4" style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}` }}>
            <Clock className="w-4 h-4" style={{ color: C.green }} />
            <span className="text-xs font-mono" style={{ color: C.green }}>15 MINUTES vs 8 WEEKS</span>
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight" style={{ color: C.text1 }}>
            Why teams switch<span style={{ color: C.green }}>.</span>
          </motion.h2>
        </motion.div>

        <div className="rounded-xl overflow-hidden" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <table className="w-full text-sm" data-testid="comparison-table">
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <th className="text-left p-5 font-mono text-xs uppercase tracking-wider w-1/4" style={{ color: C.text4 }}>Task</th>
                <th className="p-5 font-mono text-xs uppercase tracking-wider" style={{ color: C.text4 }}>Manual</th>
                <th className="p-5 font-mono text-xs uppercase tracking-wider" style={{ color: C.text4 }}>Other Tools</th>
                <th className="p-5 font-mono text-xs uppercase tracking-wider" style={{ color: C.green, background: C.greenDim }}>ZeroZone</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <ComparisonRow key={i} row={row} isLast={i === comparisonRows.length - 1} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* ROI STATS */}
    <section className="py-24 px-6" style={{ borderTop: `1px solid ${C.border}`, background: C.surface }} data-testid="roi-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {roiStats.map((stat, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} className="text-center" data-testid={`roi-stat-${i}`}>
              <div className="text-5xl md:text-6xl font-bold tracking-tighter mb-2" style={{ color: C.green }}>{stat.value}</div>
              <div className="text-sm font-semibold mb-1" style={{ color: C.text1 }}>{stat.label}</div>
              <div className="text-xs font-mono" style={{ color: C.text4 }}>{stat.desc}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* COMPLIANCE */}
    <section id="compliance" className="py-20 px-6" style={{ background: C.dark }} data-testid="compliance-badges-section">
      <div className="max-w-7xl mx-auto">
        <ComplianceHeroSection />
      </div>
    </section>

    {/* TESTIMONIALS */}
    <section className="py-24 px-6" style={{ borderTop: `1px solid ${C.border}` }} data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
          <motion.p variants={fadeUp} className="text-xs font-mono tracking-widest uppercase mb-3" style={{ color: C.text4 }}>What Teams Say</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight" style={{ color: C.text1 }}>
            Trusted by security-first teams<span style={{ color: C.green }}>.</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="rounded-xl p-7 transition-all duration-300"
              style={{ background: C.surface, border: `1px solid ${C.border}` }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHover}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
              data-testid={`testimonial-${i}`}
            >
              <p className="text-sm leading-relaxed mb-6" style={{ color: C.text2 }}>"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" style={{ border: `1px solid ${C.border}` }} />
                <div>
                  <div className="text-sm font-semibold" style={{ color: C.text1 }}>{t.name}</div>
                  <div className="text-xs font-mono" style={{ color: C.text4 }}>{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CHOOSE YOUR PATH */}
    <section id="solutions" className="py-24 px-6" style={{ background: C.dark }} data-testid="paths-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
          <motion.p variants={fadeUp} className="text-xs font-mono tracking-widest uppercase mb-3" style={{ color: C.green }}>Solutions</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ color: C.darkText1 }}>
            Choose your path<span style={{ color: C.green }}>.</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base max-w-2xl" style={{ color: C.darkText3 }}>
            Whether you're an individual architect, a growing team, or an enterprise — ZeroZone scales with your governance needs.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {paths.map((path, i) => {
            const Icon = path.icon;
            return (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="relative rounded-xl p-8 transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: C.darkSurface, border: `1px solid ${path.featured ? 'rgba(0,109,67,0.4)' : C.darkBorder}`,
                  boxShadow: path.featured ? `0 0 30px rgba(0,109,67,0.08)` : 'none'
                }}
                data-testid={`path-${path.title.toLowerCase()}`}
              >
                {path.featured && <div className="absolute -top-3 left-6 px-3 py-1 text-xs font-bold rounded-full" style={{ background: C.green, color: '#FFFFFF' }}>MOST POPULAR</div>}
                <Icon className="w-7 h-7 mb-5" style={{ color: path.featured ? C.green : C.darkText4 }} />
                <h3 className="text-xl font-bold mb-1" style={{ color: C.darkText1 }}>{path.title}</h3>
                <p className="text-xs font-mono mb-4" style={{ color: C.darkText4 }}>{path.subtitle}</p>
                <p className="text-sm mb-6 leading-relaxed" style={{ color: C.darkText3 }}>{path.desc}</p>
                <ul className="space-y-2.5 mb-8">
                  {path.capabilities.map((cap, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm" style={{ color: C.darkText2 }}>
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: C.green }} />{cap}
                    </li>
                  ))}
                </ul>
                <Button className="w-full rounded-md font-semibold"
                  style={path.featured ? { background: C.green, color: '#FFFFFF' } : { background: 'transparent', border: `1px solid ${C.darkBorder}`, color: C.darkText2 }}
                  onClick={() => path.title === 'Govern' ? onLogin() : navigate('/wizard')}
                >
                  {path.cta} <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* RESOURCES */}
    <section className="py-24 px-6" style={{ borderTop: `1px solid ${C.border}` }} data-testid="resources-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
          <motion.p variants={fadeUp} className="text-xs font-mono tracking-widest uppercase mb-3" style={{ color: C.text4 }}>Resources</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-5xl font-bold tracking-tight" style={{ color: C.text1 }}>
            Learn, Ship, Certify<span style={{ color: C.green }}>.</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {resources.map((r, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="group rounded-xl p-7 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              style={{ background: C.surface, border: `1px solid ${C.border}` }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBorder}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
              data-testid={`resource-${i}`}
            >
              <span className="inline-block px-2 py-1 rounded text-[10px] font-mono mb-4" style={{ background: C.greenDim, color: C.green }}>{r.tag}</span>
              <h3 className="text-lg font-bold mb-3 transition-colors" style={{ color: C.text1 }}>{r.title}</h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: C.text3 }}>{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-sm font-mono opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.green }}>
                Read More <ArrowRight className="w-4 h-4" />
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </>
);
